import { type UseQueryStatesKeysMap, useQueryStates } from 'nuqs';
import { useEffect, useState } from 'react';

/**
 * NOTE: この hooks の目的と背景
 * //////////////////////////////////////////////////////////////
 * 🎯 目的
 * - nuqs の useQueryStates をベースに、URL クエリと state を同期する。
 * - ただし Router の仕様で「旧ページが新しい URL で一度だけ再レンダー」されるため、
 *   クエリが無い状態で UI が初期値に戻り、一瞬だけ“ちらつき”が発生してしまう。
 * - この hooks は debounce を使って、その一瞬を抑え、
 *   スムーズな画面遷移を実現するのが目的。
 *
 * //////////////////////////////////////////////////////////////
 * ⚡ なぜちらつきが出るのか
 * (1) navigate('/projects?q=foo') や <Link> をクリックすると
 *     → ブラウザは history.pushState で「URL だけ先に変える」。
 *
 * (2) Router はまだ残っている PageA に「新しい location」を渡す。
 *     → PageA が新しい URL で一度だけ再レンダーされる。
 *
 * (3) その時点ではクエリが欠落しているため、useQueryStates は初期値を返す。
 *     → PageA 側では初期状態の UI が一瞬だけ描画される。
 *
 *   具体例:
 *   - フォーム入力: 新URLに keyword が無いため、検索欄が空文字で描画される
 *   - タブ切替   : 新URLに tab が無いため、初期タブ(overview等)が一瞬表示される
 *   - フィルタ   : status/sort が無いため、テーブルが「全件」「既定ソート」に戻る
 *   - ページング : page が無いため、1ページ目の UI が一瞬表示される
 *
 * (4) その後 React が PageA をアンマウントし、新しい PageB をマウント。
 *     → 本来表示したい正しい画面が出る。
 *
 * (5) ユーザー視点では「初期状態 → 正しい画面」という切り替わりがチラッと見える。
 *
 * //////////////////////////////////////////////////////////////
 * ✅ この hooks がやっていること
 * - useQueryStates の返す state をそのまま返しつつ、
 * - debounceMs が設定されていれば「isDebouncing=true」を一定時間立てる。
 * - UI 側では isDebouncing を見て「初期値描画を抑える」「スケルトンを出す」など制御できる。
 * - こうすることで、遷移時の一瞬のちらつきをユーザーに見せずに済む。
 *
 * //////////////////////////////////////////////////////////////
 */

type Params<KeyMap extends UseQueryStatesKeysMap> = {
  keyMap: KeyMap;
  debounceMs?: number;
  /** trueの場合はdebounce中の状態を取得する*/
  useDebounceControl?: boolean;
};

export const useDebouncedQueryStates = <KeyMap extends UseQueryStatesKeysMap>({
  keyMap,
  debounceMs,
  useDebounceControl = false,
}: Params<KeyMap>) => {
  const [searchQueryStates, setSearchQueryStates] = useQueryStates(keyMap);
  const [debouncedSearchQueryStates, setDebouncedSearchQueryStates] =
    useState(searchQueryStates);
  const [isDebouncing, setIsDebouncing] = useState(false);

  useEffect(() => {
    if (debounceMs) {
      if (useDebounceControl) {
        setIsDebouncing(true);
      }

      // 離脱時のクエリパラメータが破棄された際に、query parameterが空になり初期値が表示されるのを防ぐため
      setDebouncedSearchQueryStates(searchQueryStates);
      const timer = setTimeout(() => {
        setIsDebouncing(false);
      }, debounceMs);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [debounceMs, useDebounceControl, searchQueryStates]);

  return {
    searchQueryStates: debounceMs
      ? debouncedSearchQueryStates
      : searchQueryStates,
    setSearchQueryStates,
    isDebouncing: useDebounceControl ? isDebouncing : false,
  };
};
